import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { getFormValues } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';

import Link from 'components/common/Link';
import IconSheets from 'components/icons/IconSheets';
import Menu, { MenuItem } from 'components/common/Menu';

import formatSinglePlural from 'utils/formatSinglePlural';
import formatPrice from 'utils/formatPrice';

const styles = theme => ({
  root: {
    whiteSpace: 'nowrap',
    marginBottom: 8,
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      overflowX: 'auto',
    },
  },
  typography: {
    paddingTop: 5,
  },
  typographySeparator: {
    paddingTop: 5,
    color: theme.palette.primary.A100,
    marginLeft: 8,
    marginRight: 8,
  },
  separator: {
    height: 1,
    width: '100%',
    backgroundColor: theme.palette.primary.A100,
    margin: '0 20px',
  },
  link: {
    textTransform: 'uppercase',
    paddingTop: 8,
    paddingBottom: 8,
  },
  linkDisabled: {
    opacity: 1,
    color: theme.palette.primary.A400,
  },
  iconSheets: {
    width: 19,
    height: 22,
    marginBottom: -4,
    marginRight: 11,
  },
});

class ProductClaimHeader extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onPrintMenuItemClick = this.onPrintMenuItemClick.bind(this);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
    });
  }

  onPrintMenuItemClick(documentID) {
    const { formValues, onPrintEditPreviewButtonClick } = this.props;
    onPrintEditPreviewButtonClick({
      documentID,
      orderLine: formValues.orderLine,
    });
  }

  render() {
    const { buttonDisabled, formValues, lazyInitialized, classes } = this.props;
    const { anchorEl } = this.state;
    const totalCharge = formValues.orderLine ? formValues.orderLine
      .reduce((accumulator, currentValue) => {
        const charge = currentValue.Charge ? Number(currentValue.Charge.replace(/[,$]+/g, '')) : 0;
        const units = currentValue.Units ? currentValue.Units : 0;
        return accumulator + (charge * units);
      }, 0) : 0;
    const totalCost = formValues.orderLine ? formValues.orderLine
      .reduce((accumulator, currentValue) => {
        const cost = currentValue.Cost ? Number(currentValue.Cost.replace(/[,$]+/g, '')) : 0;
        const units = currentValue.Units ? currentValue.Units : 0;
        return accumulator + (cost * units);
      }, 0) : 0;
    const open = Boolean(anchorEl);
    return (
      <Toolbar
        className={classes.root}
        disableGutters
      >
        {formValues.ID &&
          <Fragment>
            <Typography
              className={classes.typography}
              variant="subheading"
            >
              Order #{formValues.ID}
            </Typography>
            <Typography
              className={classes.typographySeparator}
              color="secondary"
            >
              |
            </Typography>
          </Fragment>
        }
        {lazyInitialized && formValues.orderLine && formValues.orderLine.length > 0 &&
          <Fragment>
            <Typography
              className={classes.typography}
              color="secondary"
            >
              {formValues.orderLine.length} {formatSinglePlural({
                quantity: formValues.orderLine.length,
                single: 'Line',
                plural: 'Lines',
              })}
            </Typography>
            <Typography
              className={classes.typographySeparator}
              color="secondary"
            >
              |
            </Typography>
            <Typography
              className={classes.typography}
              color="secondary"
            >
              Charge: {formatPrice(totalCharge)}
            </Typography>
            <Typography
              className={classes.typographySeparator}
              color="secondary"
            >
              |
            </Typography>
            <Typography
              className={classes.typography}
              color="secondary"
            >
              Cost: {formatPrice(totalCost)}
            </Typography>
            <Typography
              className={classes.typographySeparator}
              color="secondary"
            >
              |
            </Typography>
            <Typography
              className={classes.typography}
              color="secondary"
            >
              Profit: {formatPrice(totalCharge - totalCost)}
            </Typography>
          </Fragment>
        }
        <div className={classes.separator} role="presentation" />
        <div>
          <Link
            className={classNames(classes.link, {
              [classes.linkDisabled]: buttonDisabled,
            })}
            color="primary"
            bold
            size="lg"
            disabled={buttonDisabled}
            aria-label="More"
            aria-owns={open ? 'form1500-menu' : null}
            aria-haspopup="true"
            onClick={this.onMenuButtonClick}
          >
            <IconSheets className={classes.iconSheets} />
            1500 FORM
          </Link>
          <Menu
            id="form1500-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={this.onMenuClose}
            list
            maxHeight={130}
          >
            <MenuItem
              onClick={() => this.onPrintMenuItemClick(12)}
            >
              1500 Form
            </MenuItem>
            <MenuItem
              onClick={() => this.onPrintMenuItemClick(40)}
            >
              150003 Medicaid Form
            </MenuItem>
          </Menu>
        </div>
      </Toolbar>
    );
  }
}

ProductClaimHeader.propTypes = {
  formValues: PropTypes.shape({
    orderLine: PropTypes.arrayOf(PropTypes.shape({
      'HCPC S Code': PropTypes.string,
    })),
    'Primary Insurance ID': PropTypes.number,
  }),
  buttonDisabled: PropTypes.bool,
  onPrintEditPreviewButtonClick: PropTypes.func,
  lazyInitialized: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
    separator: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    linkDisabled: PropTypes.string.isRequired,
    iconSheets: PropTypes.string.isRequired,
  }).isRequired,
};

ProductClaimHeader.defaultProps = {
  buttonDisabled: false,
  formValues: {},
  onPrintEditPreviewButtonClick: () => {},
  lazyInitialized: true,
};

const mapStateToProps = (store, ownProps) => {
  const formValues = getFormValues(ownProps.form)(store);
  return {
    formValues,
  };
};

export default connect(mapStateToProps)(
  withStyles(styles)(ProductClaimHeader),
);



// WEBPACK FOOTER //
// ./src/components/workflow/ProductClaim/ProductClaimHeader.js